export class BrowserStorage {
  private static localStorage: Storage = window.localStorage;
  private static sessionStorage: Storage = window.sessionStorage;

  /**
   * Sets a boolean value in local storage under the specified key.
   * @param key The key to store the boolean value under.
   * @param value The boolean value to store.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   */
  public static setBoolean(key: string, value: boolean, sessionOnly = true): void {
    if (sessionOnly) this.sessionStorage.setItem(key, String(value));
    else this.localStorage.setItem(key, String(value));
  }

  /**
   * Retrieves a boolean value from local storage based on the specified key.
   * @param key The key to retrieve the boolean value from.
   * @param default The default value to return if the key doesn't exist.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   * @returns The boolean value stored under the given key, or null if the key doesn't exist.
   */
  public static getBoolean(key: string, _default = null, sessionOnly = true): boolean | null {
    const value = sessionOnly ? this.sessionStorage.getItem(key) : this.localStorage.getItem(key);
    if (value == null) return _default;
    return value === "true";
  }

  /**
   * Sets an integer value in local storage under the specified key.
   * @param key The key to store the integer value under.
   * @param value The integer value to store.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   */
  public static setInt(key: string, value: number, sessionOnly = true): void {
    if (sessionOnly) this.sessionStorage.setItem(key, String(value));
    else this.localStorage.setItem(key, String(value));
  }

  /**
   * Retrieves an integer value from local storage based on the specified key.
   * @param key The key to retrieve the integer value from.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   * @returns The integer value stored under the given key, or null if the key doesn't exist.
   */
  public static getInt(key: string, sessionOnly = true): number | null {
    const value = sessionOnly ? this.sessionStorage.getItem(key) : this.localStorage.getItem(key);
    if (value == null) return null;
    return parseInt(value, 10);
  }

  /**
   * Sets a value in local storage under the specified key.
   * @param key The key to store the value under.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   * @param value The integer value to store.
   */
  public static setString(key: string, value: string, sessionOnly = true): void {
    if (value == undefined) return;
    if (sessionOnly) this.sessionStorage.setItem(key, value);
    else this.localStorage.setItem(key, value);
  }

  /**
   * Retrieves a value from local storage based on the specified key.
   * @param key The key to retrieve the integer value from.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   * @returns The value stored under the given key, or null if the key doesn't exist.
   */
  public static getString(key: string, sessionOnly = true): string | null {
    if (sessionOnly) return this.sessionStorage.getItem(key);
    else return this.localStorage.getItem(key);
  }

  /**
   * Removes the key/value pair with the given key, if a key/value pair with the given key exists.
   * @param sessionOnly True if you want to use sessionStorage instead of localStorage. Default false
   */
  public static delete(key: string, sessionOnly = true): void {
    if (sessionOnly) this.sessionStorage.removeItem(key);
    else this.localStorage.removeItem(key);
  }
}
